"use client";

import Link from "next/link";
import Image from "next/image";
import { ArrowRight, ChevronDown } from "lucide-react";
import { useState, useRef } from "react";

type Industry = {
  title: string;
  product: string;
  href: string;
  features: string[];
  image: string; // optional machen
};

const industries: Industry[] = [
  {
    title: "Brautmode",
    product: "Cashtex",
    href: "/kassensysteme/brautmode",
    image: "/industries/Brautmode.png",
    features: [
      "Terminbasierte Beratung und Anproben",
      "Anzahlungen und Reservierungen ohne Zettelwirtschaft",
      "Änderungen und Maßanpassungen übersichtlich dokumentiert",
      "Individuelle Aufträge pro Kundin statt Standardverkauf",
    ],
  },
  {
    title: "Einzelhandel – Getränke",
    product: "Cashtex",
    href: "/kassensysteme/getraenke",
    image: "/industries/Getraenkehandel.png",
    features: [
      "Pfandverwaltung ohne manuelles Nachrechnen",
      "Kisten- und Leergutprozesse direkt integriert",
      "Schnelle Mengenauswahl für typische Verkäufe",
      "Barcode-optimierte Abläufe für hohe Geschwindigkeit",
    ],
  },
  {
    title: "Friseur & Kosmetik",
    product: "Cashtex",
    href: "/kassensysteme/friseur-kosmetik",
    image: "/industries/friseur.jpg",
    features: [
      "Terminverwaltung ohne externe Tools",
      "Mitarbeiterzuweisung pro Termin und Leistung",
      "Dienstleistungen und Produkte kombinierbar",
      "Trinkgeld und Provisionen automatisch berücksichtigt",
    ],
  },
  {
    title: "Großhandel",
    product: "ModeProfi",
    href: "/modeprofi",
    image: "/industries/hero.jpg",
    features: [
      "Individuelle Preislisten pro Kunde",
      "B2B Bestellprozesse ohne Medienbrüche",
      "Mengenstaffeln direkt im Verkauf integriert",
      "Schnelle Nachbestellungen und Wiederholaufträge",
    ],
  },
  {
    title: "Messe & Eventverkauf",
    product: "Office4Trade",
    href: "/office4trade",
    image: "/industries/cashtex_schuhe.jpg",
    features: [
      "Offlinefähig bei instabiler Verbindung",
      "Extrem schnelle Bedienung für Stoßzeiten",
      "Mobiles Kassieren ohne festen Arbeitsplatz",
      "Minimaler Setup-Aufwand vor Ort",
    ],
  },
];

export default function SpecialFeaturesOverview() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const contentRefs = useRef<(HTMLDivElement | null)[]>([]);

  const toggle = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section className="py-16 md:py-24 px-6 max-w-6xl mx-auto">
      <div className="mb-16 text-center">
        <h2 className="text-3xl md:text-4xl font-semibold mb-4">
          Spezielle Funktionen für jede Branche
        </h2>
        <p className="text-muted-foreground text-base md:text-lg max-w-2xl mx-auto">
          Jede Branche hat eigene Anforderungen. Unsere Lösungen sind darauf
          ausgelegt, genau diese im Alltag abzubilden.
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-8">
        {industries.map((industry, index) => {
          const isOpen = openIndex === index;
          const preview = industry.features.slice(0, 2);

          return (
            <div
              key={index}
              className="border rounded-2xl overflow-hidden transition-all hover:shadow-md bg-white shadow-sm"
            >
              {/* BILD */}
              <div className="relative h-48 w-full">
                <Image
                  src={industry.image}
                  alt={industry.title}
                  fill
                  className="object-cover"
                />
              </div>

              {/* INHALT */}
              <div className="p-6 md:p-7">
                {/* TITLE */}
                <h3 className="text-xl font-semibold mb-4">
                  {industry.title}{" "}
                  <span className="text-brand-primary font-medium">
                    ({industry.product})
                  </span>
                </h3>

                {/* PREVIEW */}
                {!isOpen && (
                  <ul className="space-y-2 text-base font-medium mb-4">
                    {preview.map((f, i) => (
                      <li key={i}>✓ {f}</li>
                    ))}
                  </ul>
                )}

                {/* EXPAND (SMOOTH) */}
                <div
                  ref={(el) => {
                    contentRefs.current[index] = el;
                  }}
                  style={{
                    height: isOpen
                      ? contentRefs.current[index]?.scrollHeight
                        ? `${contentRefs.current[index]?.scrollHeight}px`
                        : "auto"
                      : "0px",
                  }}
                  className="overflow-hidden transition-all duration-300 ease-in-out"
                >
                  <ul className="space-y-3 text-base font-medium mb-4 pt-2">
                    {industry.features.map((f, i) => (
                      <li key={i}>✓ {f}</li>
                    ))}
                  </ul>
                </div>

                {/* ACTION ROW */}
                <div className="flex items-center justify-between mt-4">
                  <button
                    onClick={() => toggle(index)}
                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition"
                  >
                    {isOpen ? "Weniger" : "Details"}
                    <ChevronDown
                      size={16}
                      className={`transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                    />
                  </button>

                  <Link
                    href={industry.href}
                    className="flex items-center gap-2 text-primary font-semibold group"
                  >
                    <span>Mehr erfahren</span>
                    <ArrowRight className="transition-transform group-hover:translate-x-1" />
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
